import React, { useState, useEffect } from "react";
import Section from "./layouts/Section";
import { SiteWide } from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Material-UI Components
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Button,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip,
    Container,
    useTheme,
    useMediaQuery,
    alpha,
    Stack,
    Paper,
    Badge,
    Skeleton
} from '@mui/material';

// Material-UI Icons
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Security as SecurityIcon,
    Lock as LockIcon,
    VerifiedUser as VerifiedUserIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Fingerprint as FingerprintIcon,
    Shield as ShieldIcon,
    AccountCircle as AccountCircleIcon,
    VpnKey as VpnKeyIcon,
    AdminPanelSettings as AdminPanelSettingsIcon
} from '@mui/icons-material';

// Redux and utilities
import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../redux/selectors/index';
import { notify } from '../utility/Helper1';
import { Eye, EyeOff, Copy, Wallet, Shield, TrendingDown, AlertCircle, CheckCircle, History, User, Mail, Phone, MapPin, Settings, Key, Smartphone, UserCheck } from 'lucide-react';

function ChangePassword() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State management
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    
    // Form states
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    
    // UI states
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showPin, setShowPin] = useState(false);
    
    // Custom theme colors for neon accents
    const neonColors = {
        primary: '#00D4FF',
        secondary: '#7C3AED',
        accent: '#F59E0B',
        success: '#10B981',
        warning: '#F59E0B',
        error: '#EF4444',
        surface: '#1F2937',
        surfaceVariant: '#374151'
    };

    // Password change handler
    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (!password || !confirmPassword) {
            notify('Please fill in all password fields', 0);
            return;
        }
        if (password !== confirmPassword) {
            notify('Passwords do not match', 0);
            return;
        }
        if (password.length < 8) {
            notify('Password must be at least 8 characters long', 0);
            return;
        }
        if (!withdrawalPin) {
            notify('Please enter your PIN', 0);
            return;
        }
        setLoading(true);
        setLoadingText('Updating password...');
        
        dispatch(authActions.onLogin("account/change_password", {password, withdrawalPin, googleAuth: 0}, response => {
            if (response.code === 1) {
                setPassword('');
                setConfirmPassword('');
                setWithdrawalPin('');
            }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    return (
        <SiteWide id="main" title="Change Password">
            <Section loading={loading} overlayText={loadingText} title="Change Password">
                <div className="min-h-screen p-4">
                    <div className="max-w-4xl mx-auto pt-8">
                        {/* Modern Header */}
                        <div className="text-center mb-12">
                            <h1 className="text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                                Change Password
                            </h1>
                            <p className="text-xl text-blue-200">
                                Update your account password for enhanced security
                            </p>
                        </div>

                        <div className="flex flex-col gap-8">
                            {/* Enhanced Password Change Card */}
                            <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-8 shadow-2xl">
                                <div className="relative overflow-hidden">
                                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500"></div>
                                    <div className="flex items-center mb-8">
                                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                                            <Key className="text-white" size={28} />
                                        </div>
                                        <div>
                                            <h2 className="text-3xl font-bold text-white mb-2">Password Security</h2>
                                            <p className="text-gray-300">Keep your account secure with a strong password</p>
                                        </div>
                                    </div>
                                    
                                    <form onSubmit={handlePasswordChange} className="space-y-6">
                                        <div>
                                            <label className="block text-white font-medium mb-3 text-lg">New Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-blue-400 transition-all duration-300"
                                                    placeholder="Enter new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                                >
                                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                            <p className="text-gray-400 text-sm mt-2">Password must be at least 8 characters long</p>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-white font-medium mb-3 text-lg">Confirm Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-blue-400 transition-all duration-300"
                                                    placeholder="Confirm new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                                >
                                                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-white font-medium mb-3 text-lg">PIN Verification</label>
                                            <div className="relative">
                                                <input
                                                    type={showPin ? 'text' : 'password'}
                                                    value={withdrawalPin}
                                                    onChange={(e) => setWithdrawalPin(e.target.value)}
                                                    maxLength={4}
                                                    className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-yellow-400 transition-all duration-300"
                                                    placeholder="Enter your 4-digit PIN"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPin(!showPin)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                                >
                                                    {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                            <p className="text-gray-400 text-sm mt-2">Enter your 4-digit PIN to confirm changes</p>
                                        </div>
                                        
                                        <div className="flex gap-4 pt-6">
                                            
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className={`flex-1 py-4 px-6 rounded-2xl font-semibold text-white transition-all duration-300 text-lg ${
                                                    loading 
                                                        ? 'bg-gray-500 cursor-not-allowed' 
                                                        : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-blue-500/25'
                                                }`}
                                            >
                                                {loading ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                        Updating...
                                                    </div>
                                                ) : (
                                                    'Update Password'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* Security Tips Card */}
                            <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-8 shadow-2xl">
                                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                                        <Shield className="text-white" size={24} />
                                    </div>
                                    Security Tips
                                </h3>
                                
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="flex items-start gap-4">
                                        <CheckCircle className="text-green-400 mt-1" size={20} />
                                        <div>
                                            <h4 className="text-white font-semibold mb-1">Use a Strong Password</h4>
                                            <p className="text-gray-300 text-sm">Include uppercase, lowercase, numbers, and special characters</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <CheckCircle className="text-green-400 mt-1" size={20} />
                                        <div>
                                            <h4 className="text-white font-semibold mb-1">Keep it Unique</h4>
                                            <p className="text-gray-300 text-sm">Don't reuse passwords from other accounts</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <CheckCircle className="text-green-400 mt-1" size={20} />
                                        <div>
                                            <h4 className="text-white font-semibold mb-1">Regular Updates</h4>
                                            <p className="text-gray-300 text-sm">Change your password periodically for better security</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <CheckCircle className="text-green-400 mt-1" size={20} />
                                        <div>
                                            <h4 className="text-white font-semibold mb-1">Secure Storage</h4>
                                            <p className="text-gray-300 text-sm">Use a password manager to store your passwords safely</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>
        </SiteWide>
    );
}

export default ChangePassword;